import { store } from "redux-store/store";
import { authSlice } from "redux-store/reducers";

export class ApiError extends Error {
	status: number;
	data?: unknown;

	constructor(message: string, status: number, data?: unknown) {
		super(message);
		this.status = status;
		this.data = data;
	}
}

const requestTimeout = 15000;

export const getBaseUrl = () => process.env.NEXT_PUBLIC_BACKEND_URL || "/api";

// Helper function to refresh token
const refreshAccessToken = async (): Promise<string> => {
	const BACKEND_URL = getBaseUrl();
	const stored = JSON.parse(localStorage.getItem("auth/user") || "{}");
	const refreshToken = stored?.refreshToken;

	if (!refreshToken) {
		throw new Error("No refresh token available");
	}

	const rawResponse = await fetch(`${BACKEND_URL}/users/refresh`, {
		method: "POST",
		headers: {
			Accept: "application/json",
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			refreshToken,
		}),
	});

	if (!rawResponse.ok) {
		throw new Error("Failed to refresh token");
	}

	const response = await rawResponse.json();

	if (!response.token) {
		throw new Error("No token in refresh response");
	}

	// Update storage with new tokens
	const updated = {
		...stored,
		credentials: response.token,
		refreshToken: response.refreshToken || refreshToken,
	};

	localStorage.setItem("auth/user", JSON.stringify(updated));
	store.dispatch(authSlice.actions.login(updated));

	return response.token;
};

export const apiRequest = async <T>(
	path: string,
	options: RequestInit = {}
): Promise<T> => {
	const baseUrl = getBaseUrl();
	if (!baseUrl) {
		throw new ApiError("Missing backend URL", 0);
	}

	const makeRequest = async (token?: string) => {
		const controller = new AbortController();
		const timeoutId =
			typeof window === "undefined"
				? undefined
				: window.setTimeout(() => controller.abort(), requestTimeout);

		const headers: HeadersInit = {
			Accept: "application/json",
			"Content-Type": "application/json",
			...(token ? { Authorization: `Bearer ${token}` } : {}),
			...(options.headers ?? {}),
		};

		try {
			const response = await fetch(`${baseUrl}${path}`, {
				...options,
				headers,
				signal: controller.signal,
			});
			return response;
		} finally {
			if (timeoutId !== undefined && typeof window !== "undefined") {
				window.clearTimeout(timeoutId);
			}
		}
	};

	// First attempt with current token
	let token = store.getState()?.auth?.credentials;
	let response = await makeRequest(token);

	// If 401, try refreshing token and retry
	if (response.status === 401) {
		try {
			token = await refreshAccessToken();
			response = await makeRequest(token);
		} catch (error) {
			// Refresh failed, redirect to login
			if (typeof window !== "undefined") {
				localStorage.setItem("auth/redirect", window.location.pathname);
				window.location.href = "/";
			}
			throw new ApiError("Session expired. Please login again.", 401);
		}
	}

	const data = await response.json().catch(() => ({}));
	if (!response.ok) {
		const message =
			(data as { message?: string })?.message ||
			`Request failed (${response.status})`;
		throw new ApiError(message, response.status, data);
	}
	return data as T;
};
